import { z } from "zod";
import { ActorId, Cents, IsoUtc, PropertyId, Uuid } from "./primitives";
import { CivicInfo, CommercialTemplateId, HomeStageId, PropertyType, StoreThemeId } from "./world";

/** Hex color used for the storefront/sign surfaces; validated server side. */
export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/**
 * What the world renders for a commercial property. Everything here is approved, versioned
 * presentation data; the client never fetches advertiser URLs directly.
 */
export const PlacementPresentation = z.object({
  placementId: Uuid,
  presentationVersion: z.number().int().min(1),
  businessName: z.string().min(1).max(60),
  shortDescription: z.string().max(280),
  /** Hostname of the approved destination, for display only. */
  destinationHost: z.string().max(253),
  /** Approved texture URL (sanitized derivative) or null for the generated label sign. */
  logoTextureUrl: z.string().max(500).nullable(),
  /** Short mark rendered on generated signs when no approved raster logo exists. */
  monogram: z.string().min(1).max(3),
  brandColor: HexColor,
  accentColor: HexColor,
  template: CommercialTemplateId,
  hasWebsite: z.boolean(),
  isDemo: z.boolean(),
});
export type PlacementPresentation = z.infer<typeof PlacementPresentation>;

/** A purchased item placed on a predefined anchor: in the yard (street) or inside the home (interior). */
export const PlacedItem = z.object({
  anchorId: z.string(),
  itemId: z.string(),
  rotation: z.number(),
  scope: z.enum(["yard", "interior"]).default("yard"),
});
export type PlacedItem = z.infer<typeof PlacedItem>;

/** Free cosmetic choices for a home (exterior + interior); never sold, never affect anything but looks. */
export const HomeLook = z.object({
  style: z.enum(["classic", "modern", "rustic", "cottage"]).default("classic"),
  wallColor: z.enum(["white", "cream", "sky", "sage", "blush", "sand", "slate", "brick"]).default("cream"),
  roofColor: z.enum(["charcoal", "red", "green", "blue", "brown", "slate"]).default("charcoal"),
  interiorWallColor: z.enum(["white", "cream", "sky", "sage", "blush", "sand", "slate", "brick"]).default("white"),
  floor: z.enum(["wood", "tile", "carpet", "stone"]).default("wood"),
});
export type HomeLook = z.infer<typeof HomeLook>;
export const DEFAULT_HOME_LOOK: HomeLook = HomeLook.parse({});

export const HomePresentation = z.object({
  stage: HomeStageId,
  layoutVersion: z.number().int().min(0),
  placedItems: z.array(PlacedItem),
  look: HomeLook.default(() => ({ ...DEFAULT_HOME_LOOK })),
});
export type HomePresentation = z.infer<typeof HomePresentation>;

/**
 * The owner's Open / Closed switch (ADR-0019, founder request 2026-10-13). Homes and owned stores only; Townsy's own
 * buildings (civic, attractions) and unowned property are always "open". Closed = nobody new may come in (the owner
 * always can); people already inside stay. The public DTO carries only this word, nothing about who is inside.
 */
export const VisitorAccess = z.enum(["open", "closed"]);
export type VisitorAccess = z.infer<typeof VisitorAccess>;

/** One line per setting, shown under the owner's switch. */
export const VISITOR_ACCESS_HINT: Record<VisitorAccess, string> = {
  open: "Open: anyone can come in.",
  closed: "Closed: nobody new can come in; people inside can stay.",
};
/** What visitors read on the property panel, in the directory and at the door of a closed home or store. */
export const CLOSED_TO_VISITORS_MESSAGE = "Closed: the owner isn't taking visitors right now.";
/** The notice everyone moved out by the owner's "Send visitors out" gets (ADR-0019). */
export const VISITORS_SENT_OUT_NOTICE = "The owner asked visitors to leave.";

/** PUT /api/v1/properties/:id/access — a store's Open / Closed switch (current commercial owner only). */
export const PropertyAccessUpdateRequest = z.object({ access: VisitorAccess }).strict();
export type PropertyAccessUpdateRequest = z.infer<typeof PropertyAccessUpdateRequest>;
export const PropertyAccessUpdateResponse = z.object({
  propertyId: PropertyId,
  access: VisitorAccess,
  /** False when it already had this setting (no write, no audit row, no event). */
  changed: z.boolean(),
});
export type PropertyAccessUpdateResponse = z.infer<typeof PropertyAccessUpdateResponse>;

/** POST /api/v1/properties/:id/send-visitors-out — the owner of a home or store asks everyone else inside to leave. */
export const SendVisitorsOutResponse = z.object({
  propertyId: PropertyId,
  /** Server time of the request: everyone else inside who arrived before it is moved to the street. */
  requestedAt: IsoUtc,
});
export type SendVisitorsOutResponse = z.infer<typeof SendVisitorsOutResponse>;

export const PropertyStatus = z.enum(["AVAILABLE", "OWNED", "SUSPENDED"]);
export type PropertyStatus = z.infer<typeof PropertyStatus>;

export const PermittedAction = z.enum(["visit_website", "enter", "view_stats", "report", "buy", "take_over", "edit_board", "edit_interior"]);

/**
 * Owner-written marketing text shown on the board inside a store (TV / whiteboard). Plain text only:
 * never rendered as HTML, links stay non-clickable. Versioned so concurrent edits are detected.
 */
export const InteriorBoard = z.object({
  title: z.string().min(1).max(60),
  body: z.string().max(280),
  version: z.number().int().min(1),
  updatedAt: IsoUtc,
});
export type InteriorBoard = z.infer<typeof InteriorBoard>;

/** Price options for an AVAILABLE residential plot (two independent catalog offers, not an exchange rate). */
export const PlotOffer = z.object({
  coins: z.number().int().min(0).nullable(),
  usdCents: Cents.nullable(),
});
export type PlotOffer = z.infer<typeof PlotOffer>;

export const PropertyPublic = z.object({
  id: PropertyId,
  address: z.string(),
  districtId: z.string(),
  type: PropertyType,
  status: PropertyStatus,
  version: z.number().int().min(0),
  ownershipIntervalId: Uuid.nullable(),
  /** Owner public label (display name or null when unowned / hidden). */
  ownerLabel: z.string().nullable(),
  ownerActorId: ActorId.nullable(),
  /** False while the owner has closed it to visitors (`access`), and when suspended or switched off. */
  enterable: z.boolean(),
  /** The owner's Open / Closed switch (ADR-0019): "closed" only for an owned home or store whose owner closed it. */
  access: VisitorAccess,
  /** Commercial only. */
  placement: PlacementPresentation.nullable(),
  /** Commercial only: eligible subtotal the current owner paid. */
  currentBasisCents: Cents.nullable(),
  /** Commercial only: what a new buyer must pay now. */
  nextPriceCents: Cents.nullable(),
  takeoverCount: z.number().int().min(0),
  /** Completed purchases of this commercial property (seeded demo ownership does not count). */
  acquisitionCount: z.number().int().min(0),
  /** Presentation height stage 0-5 derived from acquisitionCount: the building grows with each purchase. Never implies traffic. */
  heightStage: z.number().int().min(0).max(5),
  /** Commercial only: marketing board inside the store (null = owner has not written one). */
  interiorBoard: InteriorBoard.nullable(),
  /** Commercial only: the store interior theme (null for residential and civic properties). */
  interiorTheme: StoreThemeId.nullable(),
  /** Civic buildings only (e.g. the Real Estate office). */
  civic: CivicInfo.nullable(),
  /** Residential only: offers while the plot is AVAILABLE (bought at the Real Estate office), else null. */
  forSale: PlotOffer.nullable(),
  lastChangeAt: IsoUtc.nullable(),
  /** Residential only. */
  home: HomePresentation.nullable(),
  permittedActions: z.array(PermittedAction),
});
export type PropertyPublic = z.infer<typeof PropertyPublic>;

export const PropertyListResponse = z.object({
  worldVersion: z.number().int(),
  items: z.array(PropertyPublic),
  nextCursor: z.string().nullable(),
});
export type PropertyListResponse = z.infer<typeof PropertyListResponse>;

export const PropertyHistoryEvent = z.object({
  id: Uuid,
  /** RETURNED_TO_TOWN (ADR-0037): the owner's account was deleted and the property went back to the town for sale. */
  type: z.enum(["INITIAL_ACQUISITION", "TAKEOVER", "BRANDING_CHANGED", "SEEDED_DEMO", "RETURNED_TO_TOWN"]),
  at: IsoUtc,
  ownerLabel: z.string().nullable(),
  basisCents: Cents.nullable(),
});
export type PropertyHistoryEvent = z.infer<typeof PropertyHistoryEvent>;
export const PropertyHistoryResponse = z.object({
  propertyId: PropertyId,
  items: z.array(PropertyHistoryEvent),
  nextCursor: z.string().nullable(),
});
export type PropertyHistoryResponse = z.infer<typeof PropertyHistoryResponse>;

/** A metric value; null means "not available for this range" and the UI shows a dash + reason. */
export const MetricValue = z.number().int().min(0).nullable();
export const PropertyMetricRow = z.object({
  signImpressions: MetricValue,
  panelViews: MetricValue,
  roomEntries: MetricValue,
  outboundClicksOrdinary: MetricValue,
  outboundClicksRewarded: MetricValue,
});
export type PropertyMetricRow = z.infer<typeof PropertyMetricRow>;
export const PropertyStatsRange = z.enum(["today", "yesterday", "last7d", "last30d", "lifetime"]);
export const PropertyStatsResponse = z.object({
  propertyId: PropertyId,
  definitionsVersion: z.number().int(),
  timezone: z.literal("UTC"),
  generatedAt: IsoUtc,
  lastAggregatedAt: IsoUtc.nullable(),
  currentOwnerSince: IsoUtc.nullable(),
  ranges: z.record(
    PropertyStatsRange,
    z.object({
      from: IsoUtc,
      to: IsoUtc,
      lifetime: PropertyMetricRow,
      currentOwner: PropertyMetricRow.nullable(),
    }),
  ),
  notes: z.array(z.string()),
});
export type PropertyStatsResponse = z.infer<typeof PropertyStatsResponse>;

export const OutboundIntentRequest = z.object({
  propertyId: PropertyId,
  /** Reserved for M6: a sponsored attempt that this click belongs to. */
  attemptId: Uuid.optional(),
});
export type OutboundIntentRequest = z.infer<typeof OutboundIntentRequest>;
export const OutboundIntentResponse = z.object({
  /** Same-origin redirect path, e.g. "/out/abc123". */
  url: z.string(),
  destinationHost: z.string(),
  expiresAt: IsoUtc,
});
export type OutboundIntentResponse = z.infer<typeof OutboundIntentResponse>;
